import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { getMailCategory } from '../utils/mailCategory';
import ConfirmationModal from './ConfirmationModal';
import AlertModal from './AlertModal';
import MailListRow from './MailListRow';
import MailReadingPane from './MailReadingPane';
import './Inbox.css';
import './FolderView.css';

const isImapMail = (mail) => mail && (mail.source === 'imap' || mail.uid);
const getMailUid = (mail) => mail?.uid ?? mail?.imapUid;
const getMailKey = (mail) => String(getMailUid(mail) ?? mail?._id ?? '');
const categoryLabel = (mail) => {
  const cat = getMailCategory(mail);
  return cat ? cat.charAt(0).toUpperCase() + cat.slice(1) : 'Primary';
};

/**
 * Trash Component
 * Displays all trash mails for the logged-in user
 */
const Trash = ({ currentUser }) => {
  const [mails, setMails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMail, setSelectedMail] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mailToDelete, setMailToDelete] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [selectedMailIds, setSelectedMailIds] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Selection handlers
  const handleSelectMail = (mailId) => {
    setSelectedMailIds(prev => 
      prev.includes(mailId) 
        ? prev.filter(id => id !== mailId)
        : [...prev, mailId]
    );
  };

  const handleSelectAll = () => {
    if (selectedMailIds.length === mails.length) {
      setSelectedMailIds([]);
    } else {
      setSelectedMailIds(mails.map(getMailKey));
    }
  };

  const handleDeselectAll = () => {
    setSelectedMailIds([]);
  };

  const restoreMailRequest = (mail) => {
    if (isImapMail(mail)) {
      const uid = getMailUid(mail);
      if (!uid) return Promise.reject(new Error('Missing IMAP message uid'));
      return axiosInstance.post(`/api/mail/trash/${uid}/restore`);
    }
    if (!mail?._id) return Promise.reject(new Error('Missing message id'));
    return axiosInstance.patch(`/api/mail/restore/${mail._id}`);
  };

  const handleRestoreMail = async (e, mailOrKey) => {
    e?.stopPropagation?.();
    const mail =
      typeof mailOrKey === 'object' && mailOrKey
        ? mailOrKey
        : mails.find((m) => getMailKey(m) === String(mailOrKey));
    if (!mail) return;

    const key = getMailKey(mail);
    const cat = categoryLabel(mail);
    try {
      const response = await restoreMailRequest(mail);
      if (response.data.success) {
        setMails((prev) => prev.filter((m) => getMailKey(m) !== key));
        setSelectedMailIds((prev) => prev.filter((id) => id !== key));
        if (selectedMail && getMailKey(selectedMail) === key) setSelectedMail(null);
        setAlertMessage(`Restored to Inbox → ${response.data.data?.category ? categoryLabel(response.data.data) : cat}`);
        setShowAlert(true);
        window.dispatchEvent(new CustomEvent('inboxUpdated'));
      } else {
        setAlertMessage(response.data.message || 'Failed to restore mail');
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Error restoring mail:', error);
      setAlertMessage(error.response?.data?.message || 'Error restoring mail. Please try again.');
      setShowAlert(true);
    }
  };

  const handleBulkRestore = async () => {
    if (selectedMailIds.length === 0) {
      setAlertMessage('Please select at least one mail to restore');
      setShowAlert(true);
      return;
    }
    try {
      const selectedMails = mails.filter((mail) => selectedMailIds.includes(getMailKey(mail)));
      await Promise.all(selectedMails.map((mail) => restoreMailRequest(mail)));
      const keys = new Set(selectedMails.map(getMailKey));
      setMails((prev) => prev.filter((m) => !keys.has(getMailKey(m))));
      if (selectedMail && keys.has(getMailKey(selectedMail))) setSelectedMail(null);
      setSelectedMailIds([]);
      setAlertMessage(`Restored ${selectedMails.length} mail(s) to their original Inbox category`);
      setShowAlert(true);
      window.dispatchEvent(new CustomEvent('inboxUpdated'));
    } catch (error) {
      console.error('Error restoring mails:', error);
      setAlertMessage(error.response?.data?.message || 'Error restoring mails. Please try again.');
      setShowAlert(true);
      fetchTrash(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMailIds.length === 0) {
      setAlertMessage('Please select at least one mail to delete');
      setShowAlert(true);
      return;
    }
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedMailIds.length === 0) return;
    
    setShowBulkDeleteConfirm(false); // Close modal immediately

    try {
      const selectedMails = mails.filter((mail) => selectedMailIds.includes(getMailKey(mail)));
      const deletePromises = selectedMails.map((mail) => {
        if (isImapMail(mail)) {
          const uid = getMailUid(mail);
          return axiosInstance.delete(`/api/mail/trash/${uid}`);
        }
        return axiosInstance.delete(`/api/mail/delete/${mail._id}`);
      });
      await Promise.all(deletePromises);

      setMails(mails.filter((m) => !selectedMailIds.includes(getMailKey(m))));
      if (selectedMail && selectedMailIds.includes(getMailKey(selectedMail))) {
        setSelectedMail(null);
      }
      setSelectedMailIds([]);
      fetchTrash();
    } catch (error) {
      console.error('Error deleting mails:', error);
      setAlertMessage('Error deleting mails. Please try again.');
      setShowAlert(true);
    }
  };

  const handleBulkDownload = () => {
    if (selectedMailIds.length === 0) return;
    const selectedMails = mails.filter((m) => selectedMailIds.includes(getMailKey(m)));
    selectedMails.forEach(mail => {
      const mailContent = `From: ${mail.from}\nTo: ${mail.to}\nSubject: ${mail.subject}\nDate: ${formatFullDate(mail.createdAt)}\n\n${mail.body}`;
      const blob = new Blob([mailContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${mail.subject || 'mail'}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  };

  useEffect(() => {
    if (currentUser) {
      fetchTrash();
      
      // Auto-refresh trash mails every 30 seconds (without showing loading spinner)
      const interval = setInterval(() => {
        fetchTrash(false);
      }, 30000); // 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  const fetchTrash = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    setError('');

    try {
      const response = await axiosInstance.get('/api/mail/trash');
      if (response.data.success) {
        setMails(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to fetch trash mails');
      }
    } catch (error) {
      console.error('Error fetching trash mails:', error);
      setError(error.response?.data?.message || 'Error fetching trash mails. Please try again.');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleMailClick = async (mail) => {
    const mailKey = getMailKey(mail);
    setSelectedMail(mail);

    if (isImapMail(mail)) {
      const uid = getMailUid(mail);
      try {
        const detail = await axiosInstance.get(`/api/mail/server/message/${uid}`);
        if (detail.data.success) {
          setSelectedMail(detail.data.data);
        }
        if (!mail.isRead && !mail.seen) {
          await axiosInstance.patch(`/api/mail/server/read/${uid}`);
          setMails((prev) =>
            prev.map((m) => (getMailKey(m) === mailKey ? { ...m, isRead: true, seen: true } : m))
          );
        }
      } catch (error) {
        console.error('Error loading trash message:', error);
      }
      return;
    }

    if (!mail.isRead) {
      try {
        await axiosInstance.patch(`/api/mail/mark-read/${mail._id}`);
        setMails(mails.map((m) => (m._id === mail._id ? { ...m, isRead: true } : m)));
        setSelectedMail({ ...mail, isRead: true });
      } catch (error) {
        console.error('Error marking mail as read:', error);
      }
    }
  };

  const handleDeletePermanently = async (e, mailId) => {
    e.stopPropagation();
    setMailToDelete(mailId);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePermanently = async () => {
    if (!mailToDelete) return;

    const deletingId = mailToDelete;
    setShowDeleteConfirm(false);

    const targetMail = mails.find((mail) => getMailKey(mail) === String(deletingId));

    try {
      let response;
      if (targetMail && isImapMail(targetMail)) {
        const uid = getMailUid(targetMail);
        response = await axiosInstance.delete(`/api/mail/trash/${uid}`);
      } else {
        response = await axiosInstance.delete(`/api/mail/delete/${deletingId}`);
      }

      if (response.data.success) {
        setMails(mails.filter((m) => getMailKey(m) !== String(deletingId)));
        if (selectedMail && getMailKey(selectedMail) === String(deletingId)) {
          setSelectedMail(null);
        }
      } else {
        setAlertMessage(response.data.message || 'Failed to delete mail');
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Error deleting mail:', error);
      setAlertMessage(error.response?.data?.message || 'Error deleting mail. Please try again.');
      setShowAlert(true);
    } finally {
      setMailToDelete(null);
    }
  };

  const handleCloseMail = () => {
    setSelectedMail(null);
  };

  const formatFullDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="inbox-container">
        <div className="loading">Loading trash...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inbox-container">
        <div className="error-message">{error}</div>
        <button onClick={fetchTrash} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`inbox-container premium-mail-dashboard ${selectedMail ? 'mail-detail-open' : ''}`}>
      <div className="inbox-header">
        <div>
          <p className="inbox-eyebrow">ATOZASMAIL Workspace</p>
          <h2>Trash</h2>
        </div>
        <div className="inbox-header-actions">
          <p className="mail-count">
            {mails.length} {mails.length === 1 ? 'mail' : 'mails'}
          </p>
        </div>
      </div>

      <div className={`inbox-content reading-pane-no-split gmail-split-layout${selectedMail ? ' has-detail-open full-detail-view' : ''}`}>
        <div className={`mail-list-container ${selectedMail ? 'hidden' : 'full-width'}`}>
          {mails.length > 0 && (
            <div className="mail-toolbar">
              <div className="toolbar-left">
                <button 
                  className="toolbar-btn checkbox-btn"
                  onClick={selectedMailIds.length > 0 ? handleDeselectAll : handleSelectAll}
                  title={selectedMailIds.length > 0 ? "Deselect all" : "Select all"}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    {selectedMailIds.length > 0 && (
                      <line x1="7" y1="12" x2="17" y2="12" strokeLinecap="round"/>
                    )}
                  </svg>
                  {selectedMailIds.length > 0 && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="dropdown-icon">
                      <path d="M7 10l5 5 5-5z"/>
                    </svg>
                  )}
                </button>
                
                <button 
                  className="toolbar-btn"
                  onClick={fetchTrash}
                  title="Refresh"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 4v6h-6"/>
                    <path d="M1 20v-6h6"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                </button>
                
                {selectedMailIds.length > 0 && (
                  <>
                    <button 
                      className="toolbar-btn"
                      onClick={handleBulkRestore}
                      title="Restore to Inbox"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                        <path d="M3 3v5h5"/>
                      </svg>
                    </button>

                    <button 
                      className="toolbar-btn"
                      onClick={handleBulkDownload}
                      title="Download"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                    </button>
                    
                    <button 
                      className="toolbar-btn"
                      onClick={handleBulkDelete}
                      title="Delete permanently"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
          <div className="mail-list">
            {mails.length === 0 ? (
              <div className="empty-inbox">
                <p>Trash is empty</p>
                <p className="empty-hint">Deleted mails will appear here</p>
              </div>
            ) : (
              mails.map((mail) => {
                const mailKey = getMailKey(mail);
                return (
                  <MailListRow
                    key={mailKey}
                    mail={mail}
                    mailKey={mailKey}
                    isChecked={selectedMailIds.includes(mailKey)}
                    isOpen={selectedMail && getMailKey(selectedMail) === mailKey}
                    isUnread={!mail.isRead && !mail.seen}
                    onOpen={handleMailClick}
                    onToggleCheck={handleSelectMail}
                    actions={[
                      {
                        key: 'restore',
                        title: `Restore to ${categoryLabel(mail)}`,
                        icon: (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z" />
                          </svg>
                        ),
                        onClick: (e, m) => handleRestoreMail(e, m),
                      },
                      {
                        key: 'delete',
                        title: 'Delete permanently',
                        icon: (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                          </svg>
                        ),
                        onClick: (e, m) => handleDeletePermanently(e, getMailKey(m)),
                      },
                    ]}
                  />
                );
              })
            )}
          </div>
        </div>

        {selectedMail && (
          <MailReadingPane
            mail={selectedMail}
            folderLabel={`Trash · was ${categoryLabel(selectedMail)}`}
            onClose={handleCloseMail}
            headerActions={[
              {
                key: 'restore',
                title: `Restore to ${categoryLabel(selectedMail)}`,
                icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z" />
                  </svg>
                ),
                onClick: (e, m) => handleRestoreMail(e, m),
              },
              {
                key: 'delete',
                title: 'Delete permanently',
                icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                  </svg>
                ),
                onClick: (e, m) => handleDeletePermanently(e, getMailKey(m)),
              },
            ]}
          />
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setMailToDelete(null);
        }}
        onConfirm={confirmDeletePermanently}
        message="Are you sure you want to permanently delete this mail?"
        confirmText="OK"
        cancelText="Cancel"
      />

      <ConfirmationModal
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={confirmBulkDelete}
        message={`Are you sure you want to permanently delete ${selectedMailIds.length} mail(s)?`}
        confirmText="OK"
        cancelText="Cancel"
      />

      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        message={alertMessage}
      />
    </div>
  );
};

export default Trash;


